import { Clipboard } from '@angular/cdk/clipboard';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { MessageService, PrimeIcons } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  CreditCardPaymentSummary,
  FinancialControllerService,
  Gateway,
  PageableRequest,
  TransactionStatus
} from 'src/app/admin-api';
import {
  DropdownFilter,
  PageContent,
  TableActionButton,
  TableColumn
} from 'src/app/components/table';
import { TransactionStatusEnum } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { subscriberPendingFeeIdLabel } from 'src/app/utils/payment-display.util';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class PaymentsComponent implements OnInit {
  cols: Array<TableColumn> = [
    new TableColumn(
      'Id assinatura',
      'subscriberId',
      true,
      'number',
      '/users/subscribers/',
      'subscriberId'
    ),
    new TableColumn(
      'Assinante',
      'personName',
      true,
      'text',
      '/users/person/',
      'personId',
      true,
      'contains'
    ),
    new TableColumn('TID', 'tid', true, 'text', undefined, undefined, true),
    new TableColumn(
      'Gateway',
      'gatewayName',
      true,
      'text',
      undefined,
      undefined,
      true,
      'contains'
    ),
    new TableColumn('Valor', 'amount', false, 'currency'),
    new TableColumn('Parcelas', 'installments', false, 'text'),
    new TableColumn('Tentativas', 'attempt', true, 'number'),
    new TableColumn('Autorização', 'authorizationCode', true, 'text'),
    new TableColumn('Id Cartão', 'creditCardId', true, 'number'),
    new TableColumn('Id Pagamento CC', 'creditCardPaymentId', true, 'number'),
    new TableColumn('Tipo Pagamento Id', 'creditCardPaymentTypeId', true, 'number'),
    new TableColumn(
      'Data',
      'dateCreated',
      true,
      'date',
      undefined,
      undefined,
      true,
      'between'
    ),
    new TableColumn('Data Autorização', 'dateAuthorized', true, 'date'),
    new TableColumn('Status', 'transactionStatusName', true, 'number'),
    new TableColumn('Detalhe', 'authorizationMessage', true, 'text'),
    new TableColumn('Tipo', 'creditCardPaymentType', true, 'text'),
    new TableColumn.Builder()
      .setHeader('Id pendência')
      .setField('subscriberPendingFeeId')
      .setFilter(false)
      .setType('text')
      .setDisplayFunction((payment: CreditCardPaymentSummary) =>
        subscriberPendingFeeIdLabel(payment.subscriberPendingFeeId)
      )
      .setTooltip((payment: CreditCardPaymentSummary) =>
        payment.subscriberPendingFeeId != null
          ? 'SubscriberPendingFee vinculado a esta captura'
          : ''
      )
      .build(),
    new TableColumn('Extra', 'extraData', true, 'text'),
    new TableColumn('Gateway Id', 'gatewayId', true, 'number'),
    new TableColumn('IP', 'ipAddress', true, 'text'),
    new TableColumn('LR', 'lr', true, 'text'),
    new TableColumn('Próximo Passo', 'nextStep', true, 'text'),
    new TableColumn('NSU', 'nsu', true, 'text'),
    new TableColumn('PAN', 'pan', true, 'text'),
    new TableColumn('GUID Pagamento', 'paymentGuid', true, 'text'),
    new TableColumn('Id Pagamento', 'paymentId', true, 'number'),
    new TableColumn('Id Pessoa', 'personId', true, 'number'),
    new TableColumn('Recorrência', 'recurrence', true, 'number'),
    new TableColumn('Assinatura', 'subscriptionName', true, 'text'),
    new TableColumn('Tipo Assinatura', 'subscriptionTypeName', true, 'text'),
    new TableColumn('Status Id', 'transactionStatusId', true, 'number'),
    new TableColumn.Builder()
      .setHeader('Ação')
      .setField(' ')
      .setFilter(false)
      .setType('button')
      .build()
  ];
  dropdownFilters: {
    [field: string]: Array<DropdownFilter>;
  } = {
    transactionStatusName: []
  };
  transactionStatusList: Array<TransactionStatus> | undefined;
  gateways: Array<Gateway> | undefined;
  actionButtons = [
    new TableActionButton.Builder()
      .setIcon(PrimeIcons.COPY)
      .setAction((payment: CreditCardPaymentSummary) =>
        this.copyToClipboard(payment.nextStep)
      )
      .setSeverity('primary')
      .setSize('small')
      .setStyleClass('p-button-xs')
      .setText(true)
      .setTooltip('Copiar link de pagamento')
      .setTooltipPosition('bottom')
      .setDisplay(
        (payment: CreditCardPaymentSummary) =>
          !!payment.nextStep?.trim() &&
          [
            TransactionStatusEnum.EM_ANDAMENTO,
            TransactionStatusEnum.CRIADA
          ].includes(payment.transactionStatusId)
      )
      .build()
  ];
  defaultFilterValues: { [field: string]: any | Array<any> };
  constructor(
    public financialService: FinancialControllerService,
    private title: Title,
    private clipboard: Clipboard,
    private messageService: MessageService
  ) {
    this.title.setTitle('Pagamentos');
    this.defaultFilterValues = {
      dateCreated: this.startDates
    };
  }

  async ngOnInit(): Promise<void> {
    await Promise.all([
      this.findTransactionStatusList(),
      this.findGatewayList()
    ]);
  }

  async findTransactionStatusList(): Promise<void> {
    try {
      this.transactionStatusList = await lastValueFrom(
        this.financialService
          .findTransactionStatusList()
          .pipe(map((data) => data.result))
      );
      this.dropdownFilters['transactionStatusName'] = [
        { label: 'Todos', value: '-1' }
      ].concat(
        this.transactionStatusList?.map(
          (t) =>
            ({
              label: t.transactionStatusName,
              value: t.transactionStatusId?.toString()
            }) as { label: string; value: string }
        ) || []
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findGatewayList(): Promise<void> {
    try {
      this.gateways = await lastValueFrom(
        this.financialService
          .findGatewaysList()
          .pipe(map((data) => data.result))
      );
      this.dropdownFilters['gatewayName'] = [
        { label: 'Todos', value: '-1' }
      ].concat(
        this.gateways?.map(
          (g) =>
            ({
              label: g.gatewayName,
              value: g.gatewayId?.toString()
            }) as { label: string; value: string }
        ) || []
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findPage(
    request: PageableRequest,
    service: unknown
  ): Promise<PageContent<CreditCardPaymentSummary>> {
    const filterTransaction = request.filters?.find(
      (f) => f.field === 'transactionStatusName'
    );
    if (filterTransaction?.value) {
      if (Number(filterTransaction.value) < 0) {
        filterTransaction.value = '';
      }
      request.filters?.push({
        condition: 'equals',
        field: 'transactionStatusId',
        fieldType: 'number',
        value: filterTransaction.value
      });
    }
    const filterGateway = request.filters?.find(
      (f) => f.field === 'gatewayName'
    );
    if (filterGateway?.value) {
      if (Number(filterGateway.value) < 0) {
        filterGateway.value = '';
      }
      request.filters?.push({
        condition: 'equals',
        field: 'gatewayId',
        fieldType: 'number',
        value: filterGateway.value
      });
    }
    return lastValueFrom(
      (service as FinancialControllerService)
        .findPaymentTable(request)
        .pipe(
          map((data) => data.result as PageContent<CreditCardPaymentSummary>)
        )
    );
  }

  copyToClipboard(text: string): void {
    this.clipboard.copy(text);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Copiado para área de trasnferência'
    });
  }

  get startDates(): Array<Date> {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    return [startDate, new Date()];
  }
}
